'use client';
import { useEffect, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';

export interface RealtimeAlert {
  id: string;
  type: 'portfolio' | 'compliance' | 'transaction' | 'settlement' | 'kyc' | 'system' | 'aml';
  severity: 'info' | 'warning' | 'critical';
  title: string;
  message: string;
  ts: string;
  read: boolean;
}

interface UseRealtimeAlertsOptions {
  /** Which alert channels to subscribe to */
  channels?: Array<'portfolio' | 'compliance' | 'transactions' | 'settlements' | 'aml' | 'realtime_alerts'>;
  maxAlerts?: number;
}

/**
 * useRealtimeAlerts — subscribes to Supabase real-time channels and delivers
 * instant portfolio, compliance status, AML transaction, and settlement alerts to dashboards.
 *
 * Falls back gracefully to demo mode when Supabase tables don't exist yet.
 */
export function useRealtimeAlerts(options: UseRealtimeAlertsOptions = {}) {
  const { channels = ['portfolio', 'compliance', 'transactions', 'settlements', 'aml', 'realtime_alerts'], maxAlerts = 20 } = options;

  const [alerts, setAlerts] = useState<RealtimeAlert[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const pushAlert = useCallback((alert: Omit<RealtimeAlert, 'id' | 'ts' | 'read'>) => {
    const newAlert: RealtimeAlert = {
      ...alert,
      id: `alert-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      ts: new Date().toISOString(),
      read: false,
    };
    setAlerts((prev) => [newAlert, ...prev].slice(0, maxAlerts));
    setUnreadCount((c) => c + 1);
  }, [maxAlerts]);

  const markAllRead = useCallback(() => {
    setAlerts((prev) => prev.map((a) => ({ ...a, read: true })));
    setUnreadCount(0);
  }, []);

  const markRead = useCallback((id: string) => {
    setAlerts((prev) =>
      prev.map((a) => (a.id === id ? { ...a, read: true } : a))
    );
    setUnreadCount((c) => Math.max(0, c - 1));
  }, []);

  const dismissAlert = useCallback((id: string) => {
    setAlerts((prev) => {
      const alert = prev.find((a) => a.id === id);
      if (alert && !alert.read) setUnreadCount((c) => Math.max(0, c - 1));
      return prev.filter((a) => a.id !== id);
    });
  }, []);

  useEffect(() => {
    let supabase: any = null;
    const activeChannels: any[] = [];

    try {
      supabase = createClient();
      if (!supabase) return;

      // ── Portfolio changes ──────────────────────────────────────────────────
      if (channels.includes('portfolio')) {
        const portfolioCh = supabase
          .channel('rt-portfolio')
          .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'investor_portfolios' }, (payload: any) => {
            pushAlert({
              type: 'portfolio',
              severity: 'info',
              title: 'Portfolio Updated',
              message: `Portfolio value changed for investor ${payload.new?.investor_id?.slice(0, 8) || 'unknown'}`,
            });
          })
          .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'investment_commitments' }, (payload: any) => {
            pushAlert({
              type: 'portfolio',
              severity: 'info',
              title: 'New Investment Commitment',
              message: `New commitment of $${Number(payload.new?.amount || 0).toLocaleString()} received`,
            });
          })
          .subscribe((status: string) => {
            if (status === 'SUBSCRIBED') setIsConnected(true);
          });
        activeChannels.push(portfolioCh);
      }

      // ── Compliance status changes ──────────────────────────────────────────
      if (channels.includes('compliance')) {
        const complianceCh = supabase
          .channel('rt-compliance')
          .on('postgres_changes', { event: '*', schema: 'public', table: 'kyc_applications' }, (payload: any) => {
            const status = payload.new?.status;
            pushAlert({
              type: 'compliance',
              severity: status === 'rejected' ? 'critical' : status === 'flagged' || status === 'edd_required' ? 'warning' : 'info',
              title: 'KYC Status Update',
              message: `KYC application ${payload.new?.application_ref || ''} status: ${status || 'updated'}`,
            });
          })
          .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'compliance_holds' }, (payload: any) => {
            pushAlert({
              type: 'compliance',
              severity: 'critical',
              title: 'Compliance Hold Placed',
              message: `Hold placed on ${payload.new?.entity_name || 'entity'}: ${payload.new?.reason || 'compliance review required'}`,
            });
          })
          .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'compliance_holds' }, (payload: any) => {
            if (payload.new?.status === 'resolved') {
              pushAlert({
                type: 'compliance',
                severity: 'info',
                title: 'Compliance Hold Resolved',
                message: `Hold on ${payload.new?.entity_name || 'entity'} has been resolved`,
              });
            }
          })
          .subscribe((status: string) => {
            if (status === 'SUBSCRIBED') setIsConnected(true);
          });
        activeChannels.push(complianceCh);
      }

      // ── AML transaction triggers ───────────────────────────────────────────
      if (channels.includes('aml')) {
        const amlCh = supabase
          .channel('rt-aml')
          .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'aml_alerts' }, (payload: any) => {
            const severity = payload.new?.severity;
            pushAlert({
              type: 'aml',
              severity: severity === 'critical' ? 'critical' : severity === 'high' ? 'warning' : 'info',
              title: `AML Alert: ${payload.new?.rule_code || 'Rule Triggered'}`,
              message: `${payload.new?.entity_name || 'Entity'} — ${payload.new?.alert_type || 'suspicious activity'} flagged`,
            });
          })
          .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'aml_alerts' }, (payload: any) => {
            const newStatus = payload.new?.status;
            if (newStatus === 'escalated') {
              pushAlert({
                type: 'aml',
                severity: 'critical',
                title: 'AML Alert Escalated',
                message: `${payload.new?.alert_ref || 'Alert'} escalated — ${payload.new?.entity_name || 'entity'} requires immediate review`,
              });
            } else if (newStatus === 'str-pending') {
              pushAlert({
                type: 'aml',
                severity: 'critical',
                title: 'STR Filing Pending',
                message: `Suspicious Transaction Report pending for ${payload.new?.entity_name || 'entity'}`,
              });
            }
          })
          .subscribe((status: string) => {
            if (status === 'SUBSCRIBED') setIsConnected(true);
          });
        activeChannels.push(amlCh);
      }

      // ── Transaction alerts ─────────────────────────────────────────────────
      if (channels.includes('transactions')) {
        const txnCh = supabase
          .channel('rt-transactions')
          .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'transactions' }, (payload: any) => {
            const amount = Number(payload.new?.amount || 0);
            pushAlert({
              type: 'transaction',
              severity: amount > 1000000 ? 'warning' : 'info',
              title: 'New Transaction',
              message: `${payload.new?.txn_type || 'Transaction'} of $${amount.toLocaleString()} — ${payload.new?.description || ''}`,
            });
          })
          .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'transactions' }, (payload: any) => {
            if (payload.new?.status === 'failed') {
              pushAlert({
                type: 'transaction',
                severity: 'critical',
                title: 'Transaction Failed',
                message: `TXN ${payload.new?.txn_ref || payload.new?.id?.slice(0, 8)} failed — manual review required`,
              });
            }
          })
          .subscribe((status: string) => {
            if (status === 'SUBSCRIBED') setIsConnected(true);
          });
        activeChannels.push(txnCh);
      }

      // ── Settlement batch updates ───────────────────────────────────────────
      if (channels.includes('settlements')) {
        const settlementCh = supabase
          .channel('rt-settlements')
          .on('postgres_changes', { event: '*', schema: 'public', table: 'settlement_batches' }, (payload: any) => {
            const status = payload.new?.status;
            pushAlert({
              type: 'settlement',
              severity: status === 'failed' || status === 'rejected' ? 'critical' : status === 'pending_approval' ? 'warning' : 'info',
              title: 'Settlement Update',
              message: `Batch ${payload.new?.batch_ref || ''} status: ${status || 'updated'} — $${Number(payload.new?.amount || 0).toLocaleString()}`,
            });
          })
          .subscribe((status: string) => {
            if (status === 'SUBSCRIBED') setIsConnected(true);
          });
        activeChannels.push(settlementCh);
      }

      // ── Realtime alerts table (broadcast to all portals) ──────────────────
      if (channels.includes('realtime_alerts')) {
        const rtAlertsCh = supabase
          .channel('rt-alerts-table')
          .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'realtime_alerts' }, (payload: any) => {
            const alertType = payload.new?.alert_type;
            const severity = payload.new?.severity;
            pushAlert({
              type: alertType || 'system',
              severity: severity || 'info',
              title: payload.new?.title || 'Platform Alert',
              message: payload.new?.message || 'New platform event received',
            });
          })
          .subscribe((status: string) => {
            if (status === 'SUBSCRIBED') setIsConnected(true);
          });
        activeChannels.push(rtAlertsCh);
      }

    } catch {
      // Supabase not configured — run in demo mode without subscriptions
    }

    return () => {
      if (supabase) {
        activeChannels.forEach((ch) => supabase.removeChannel(ch));
      }
    };
  }, [channels, pushAlert]);

  return {
    alerts,
    isConnected,
    unreadCount,
    markAllRead,
    markRead,
    dismissAlert,
    pushAlert,
  };
}
